import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import BugReportIcon from '@mui/icons-material/BugReport'
import BuildIcon from '@mui/icons-material/Build'
import CloseIcon from '@mui/icons-material/Close'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import PaletteIcon from '@mui/icons-material/Palette'
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  Modal,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material'
import { useVirtualizer } from '@tanstack/react-virtual'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import packageJson from '../../package.json'
import { patchNotes } from '../patchNotes'
import { VersionBoxScroller } from '../styles/elements/styleElements'

export const version = packageJson.version

type CategoryType = 'Features' | 'Bug Fixes' | 'Style' | 'Refactors'

const categoryConfig: Record<CategoryType, { icon: React.ReactNode; color: string; label: string }> = {
  Features: {
    icon: <AutoAwesomeIcon fontSize="small" />,
    color: 'rgba(3, 138, 255, 1)',
    label: 'Features',
  },
  'Bug Fixes': {
    icon: <BugReportIcon fontSize="small" />,
    color: 'rgba(231, 109, 137, 1)',
    label: 'Bug Fixes',
  },
  Style: {
    icon: <PaletteIcon fontSize="small" />,
    color: 'rgba(238, 152, 61, 0.9)',
    label: 'Style',
  },
  Refactors: {
    icon: <BuildIcon fontSize="small" />,
    color: 'rgba(3, 201, 169, 1)',
    label: 'Refactors',
  },
}

const parseNoteText = (text: string) => {
  // Parse **scope:** format and remove trailing )
  const match = text.match(/^\*\*([^*]+)\*\*:?\s*(.+?)\)?$/)
  if (match) {
    return { scope: match[1], description: match[2].replace(/\)$/, '') }
  }
  return { scope: null, description: text.replace(/\)$/, '') }
}

interface CategorySectionProps {
  category: CategoryType
  items: string[]
}

const CategorySection = ({ category, items }: CategorySectionProps) => {
  const theme = useTheme()
  const config = categoryConfig[category]

  if (!items || items.length === 0) return null

  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, py: 1 }}>
        <Box sx={{ color: config.color, display: 'flex', alignItems: 'center' }}>{config.icon}</Box>
        <Typography
          variant="subtitle2"
          sx={{
            color: config.color,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            fontSize: '0.75rem',
          }}
        >
          {config.label}
        </Typography>
        <Chip
          label={items.length}
          size="small"
          sx={{
            height: 18,
            fontSize: '0.65rem',
            bgcolor: `${config.color}22`,
            color: config.color,
            fontWeight: 600,
          }}
        />
      </Box>
      <Stack spacing={0.75} sx={{ pl: 1 }}>
        {items.map((item, index) => {
          const { scope, description } = parseNoteText(item)
          return (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                py: 0.5,
                borderLeft: `2px solid ${config.color}33`,
                pl: 1.5,
                '&:hover': {
                  bgcolor: alpha(theme.palette.text.primary, 0.03),
                  borderLeftColor: config.color,
                },
                transition: 'all 0.2s ease',
              }}
            >
              <Box
                sx={{
                  width: 140,
                  minWidth: 140,
                  flexShrink: 0,
                }}
              >
                {scope && (
                  <Chip
                    label={scope}
                    size="medium"
                    sx={{
                      height: 20,
                      fontSize: '0.75rem',
                      bgcolor: alpha(theme.palette.text.primary, 0.08),
                      color: theme.palette.text.secondary,
                      fontWeight: 500,
                    }}
                  />
                )}
              </Box>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.primary,
                  fontSize: '0.8rem',
                  lineHeight: 1.5,
                  textAlign: 'left',
                }}
              >
                {description}
              </Typography>
            </Box>
          )
        })}
      </Stack>
    </Box>
  )
}

interface VersionEntryProps {
  versionNumber: string
  data: {
    date?: string | null
    Features?: string[]
    'Bug Fixes'?: string[]
    Style?: string[]
    Refactors?: string[]
  }
  isLatest?: boolean
  isExpanded: boolean
  onToggle: () => void
}

const VersionEntry = ({ versionNumber, data, isLatest, isExpanded, onToggle }: VersionEntryProps) => {
  const theme = useTheme()
  const totalChanges =
    (data.Features?.length || 0) +
    (data['Bug Fixes']?.length || 0) +
    (data.Style?.length || 0) +
    (data.Refactors?.length || 0)

  const hasChanges = totalChanges > 0

  return (
    <Box sx={{ bgcolor: 'transparent' }}>
      {/* Summary - clickable header */}
      <Box
        onClick={onToggle}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 1,
          py: 1.5,
          minHeight: 48,
          borderRadius: 1,
          cursor: 'pointer',
          '&:hover': {
            bgcolor: alpha(theme.palette.text.primary, 0.03),
          },
        }}
      >
        <ExpandMoreIcon
          sx={{
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s',
            color: theme.palette.text.secondary,
          }}
        />
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            fontSize: '1rem',
            color: isLatest ? theme.palette.text.highlight : theme.palette.text.primary,
          }}
        >
          v{versionNumber}
        </Typography>
        {data.date && (
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontSize: '0.75rem',
            }}
          >
            {data.date}
          </Typography>
        )}
        {isLatest && (
          <Chip
            label="Latest"
            size="small"
            sx={{
              height: 20,
              fontSize: '0.65rem',
              bgcolor: alpha(theme.palette.text.complete, 0.15),
              color: theme.palette.text.complete,
              fontWeight: 600,
            }}
          />
        )}
        {hasChanges && (
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              ml: 'auto',
              mr: 1,
            }}
          >
            {totalChanges} change{totalChanges !== 1 ? 's' : ''}
          </Typography>
        )}
      </Box>
      {/* Details - collapsible content */}
      <Collapse in={isExpanded} unmountOnExit>
        <Box sx={{ px: 2, pt: 0, pb: 2, pl: 5 }}>
          {hasChanges ? (
            <>
              <CategorySection category="Features" items={data.Features || []} />
              <CategorySection category="Bug Fixes" items={data['Bug Fixes'] || []} />
              <CategorySection category="Style" items={data.Style || []} />
              <CategorySection category="Refactors" items={data.Refactors || []} />
            </>
          ) : (
            <Typography variant="body2" sx={{ color: theme.palette.text.disabled, fontStyle: 'italic', pl: 1 }}>
              No changes documented for this version
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  )
}

const GroboticVersionControl = () => {
  const [notesOpen, setNotesOpen] = useState(false)
  const [expandedVersions, setExpandedVersions] = useState<Set<string>>(() => new Set())
  const theme = useTheme()
  const parentRef = useRef<HTMLDivElement>(null)

  const handleOpenPatchNotes = useCallback(() => {
    setNotesOpen(true)
    // Auto-expand the latest version when opening
    const firstVersion = Object.keys(patchNotes)[0]
    if (firstVersion) {
      setExpandedVersions(new Set([firstVersion]))
    }
  }, [])
  const handleClosePatchNotes = useCallback(() => setNotesOpen(false), [])

  const versionEntries = useMemo(() => Object.entries(patchNotes), [])

  const toggleVersion = useCallback((versionNum: string) => {
    setExpandedVersions(prev => {
      const next = new Set(prev)
      if (next.has(versionNum)) {
        next.delete(versionNum)
      } else {
        next.add(versionNum)
      }
      return next
    })
  }, [])

  // Virtualize the version list - only render visible items
  const rowVirtualizer = useVirtualizer({
    count: versionEntries.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 56, // Collapsed row height estimate
    overscan: 5, // Render 5 extra items above/below viewport
    enabled: notesOpen, // Only virtualize when modal is open
  })

  // Force virtualizer to recalculate when modal opens
  useEffect(() => {
    if (notesOpen) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        rowVirtualizer.measure()
      }, 50)
      return () => clearTimeout(timer)
    }
  }, [notesOpen, rowVirtualizer])

  return (
    <>
      <Box
        onClick={handleOpenPatchNotes}
        sx={{
          cursor: 'pointer',
          px: 0.75,
          py: 0.25,
          borderRadius: 1,
          fontSize: 10,
          fontWeight: 700,
          fontFamily: 'monospace',
          lineHeight: 1,
          whiteSpace: 'nowrap',
          color: theme.palette.text.highlight,
          bgcolor: alpha(theme.palette.text.highlight, 0.1),
          '&:hover': {
            bgcolor: alpha(theme.palette.text.highlight, 0.2),
          },
          transition: 'background-color 0.2s ease',
        }}
      >
        v{version}
      </Box>

      <Modal open={notesOpen} onClose={handleClosePatchNotes}>
        <VersionBoxScroller>
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              px: 1,
              py: 2,
              backgroundColor: theme.palette.background.paper,
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Stack spacing={0.5}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  background: `linear-gradient(135deg, ${theme.palette.text.primary} 0%, ${theme.palette.text.highlight} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Release Notes
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                Current version: <strong>v{version}</strong> ({versionEntries.length} releases)
              </Typography>
            </Stack>
            <IconButton
              onClick={handleClosePatchNotes}
              sx={{
                '&:hover': {
                  bgcolor: alpha(theme.palette.text.primary, 0.1),
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Virtualized Version List */}
          <Box
            ref={parentRef}
            sx={{
              flex: 1,
              overflow: 'auto',
              px: 1,
              minHeight: 0, // Important for flex child to scroll properly
            }}
          >
            <div
              style={{
                height: rowVirtualizer.getTotalSize(),
                width: '100%',
                position: 'relative',
              }}
            >
              {rowVirtualizer.getVirtualItems().map(virtualRow => {
                const [versionNum, data] = versionEntries[virtualRow.index]
                const isExpanded = expandedVersions.has(versionNum)
                return (
                  <div
                    key={versionNum}
                    data-index={virtualRow.index}
                    ref={rowVirtualizer.measureElement}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      transform: `translateY(${virtualRow.start}px)`,
                    }}
                  >
                    <VersionEntry
                      versionNumber={versionNum}
                      data={data}
                      isLatest={virtualRow.index === 0}
                      isExpanded={isExpanded}
                      onToggle={() => toggleVersion(versionNum)}
                    />
                  </div>
                )
              })}
            </div>
          </Box>
        </VersionBoxScroller>
      </Modal>
    </>
  )
}

export default GroboticVersionControl
